import { LabObjectMap, decodeLabObjects } from './labObjects'
import { useCallback, useEffect, useRef, useState } from 'react'

import { LabDetectedSource } from './labEngine'
import { LabEngine } from './useLabEngine.hook'
import { logger } from 'utils/logging'
import { parseLabSegments } from 'utils/labSdk/labSegments'

export type LabEraserObjects =
  | { imageUrl: string, status: 'loading' }
  | { imageUrl: string, status: 'ready', map: LabObjectMap }
  | { imageUrl: string, status: 'unavailable' }

/** Settle time before uploading: the photo on stage is often swapped right away (the original's URL for its local copy) */
export const LAB_OBJECTS_UPLOAD_DELAY_MS = 400

/** Detection takes about 5 s on the Lab's GPU, longer under load */
export const LAB_OBJECTS_TIMEOUT_MS = 90_000

interface UseLabEraserObjectsProps {
  isEnabled: boolean
  /** Detects without saying so: no 'loading' while it runs, so nothing is drawn over the photo meanwhile */
  isSilent?: boolean
  engine: LabEngine
  /** The photo on stage */
  imageUrl: string
  /** The photo as the file a Lab run would upload */
  getImageFile: (imageUrl: string) => Promise<File>
  /** Uploads to the platform's temp bucket (save-temporary-visual); resolves with the stored filename */
  uploadVisual: (file: File) => Promise<string>
}

/**
 * Which photo has its objects detected, and whether that shows. The photo Photo Studio opened with is detected from the
 * start, silently, whatever the tool — so its objects are ready when the eraser opens. Any later photo (an erase's
 * result, a staging) is detected once the eraser opens on it, with "Detecting objects" over it, as before.
 * Subscribers only: for the others the eraser is locked.
 */
export const resolveLabObjectsDetection = ({ isLabSdkEngine, hasSubscription, isEraserOpen, isOpenedPhoto }: {
  isLabSdkEngine: boolean
  hasSubscription: boolean
  isEraserOpen: boolean
  /** The photo on stage is the one Photo Studio opened with */
  isOpenedPhoto: boolean
}) => ({
  isEnabled: isLabSdkEngine && hasSubscription && (isEraserOpen || isOpenedPhoto),
  isSilent: isOpenedPhoto,
})

/**
 * Click-to-select for the Lab eraser (the Lab Playground's object picker). While enabled (resolveLabObjectsDetection),
 * the photo on stage is uploaded and the platform has the Lab detect its objects; once that detection FINISHED, the upload and its
 * detection are kept so Generate reuses them rather than uploading the same photo again. Until then Generate uploads
 * afresh: the platform refuses an erase naming a detection still running ("Object detection has not finished"). Keyed
 * by the photo on stage: another photo is detected again, never paired with an earlier detection. Turning off (the
 * eraser closed, Erase toggled off in All tools) keeps what was detected: back on the same photo, its objects show at
 * once and a detection still running resumes — only a change to the photo detects again. Best effort — without
 * objects the brush works as before.
 */
export const useLabEraserObjects = ({ isEnabled, isSilent = false, engine, imageUrl, getImageFile, uploadVisual }: UseLabEraserObjectsProps) => {
  const [objects, setObjects] = useState<LabEraserObjects | null>(null)
  const objectsRef = useRef(objects)
  objectsRef.current = objects
  /** Uploads and detections started, one per photo — reused rather than started twice */
  const detectionsRef = useRef(new Map<string, Promise<LabDetectedSource>>())
  /** Detections that reached FINISHED: the only ones an erase may name */
  const finishedRef = useRef(new Map<string, LabDetectedSource>())
  const dependenciesRef = useRef({ engine, getImageFile, uploadVisual })
  dependenciesRef.current = { engine, getImageFile, uploadVisual }

  const forget = useCallback((url: string, detection: Promise<LabDetectedSource>) => {
    if (detectionsRef.current.get(url) === detection) detectionsRef.current.delete(url)
  }, [])

  const detect = useCallback((url: string): Promise<LabDetectedSource> => {
    const existing = detectionsRef.current.get(url)
    if (existing) return existing
    const { engine: lab, getImageFile: toFile, uploadVisual: upload } = dependenciesRef.current
    const detection = toFile(url).then(upload).then(async (visualFilename) => ({
      visualFilename,
      objectsJobId: await lab.startJob({ tool: 'objects', body: { visualFilename } }),
    }))
    detectionsRef.current.set(url, detection)
    // A failed upload or start is tried again next time, never reused
    detection.catch(() => forget(url, detection))
    return detection
  }, [forget])

  useEffect(() => {
    // Off, the photo's upload, detection and objects are kept for when it is back on
    if (!isEnabled || !imageUrl) return undefined
    // The same photo, already detected: nothing changed, nothing to detect again
    const current = objectsRef.current
    if (current?.imageUrl === imageUrl && current.status === 'ready') return undefined
    const controller = new AbortController()
    const { signal } = controller
    const timer = setTimeout(async () => {
      setObjects({ imageUrl, status: 'loading' })
      const detection = detect(imageUrl)
      let hasDetectionFailed = false
      try {
        const source = await detection
        const lab = dependenciesRef.current.engine
        try {
          await lab.waitForJob(source.objectsJobId, { signal, timeoutMs: LAB_OBJECTS_TIMEOUT_MS })
        } catch (error) {
          hasDetectionFailed = !signal.aborted
          throw error
        }
        finishedRef.current.set(imageUrl, source)
        const response = await lab.fetchResult(source.objectsJobId, signal)
        const result = parseLabSegments(await response.json())
        if (result.status !== 'ready') throw new Error(`Lab objects: detection ${result.status}`)
        const map = await decodeLabObjects(result.segments)
        if (!signal.aborted) setObjects({ imageUrl, status: 'ready', map })
      } catch (error) {
        // A detection that failed is not one an erase can name: Generate uploads the photo afresh instead
        if (hasDetectionFailed) forget(imageUrl, detection)
        if (signal.aborted) return
        logger.warn('Lab engine: object selection unavailable, brush only', error, {
          context: { component: 'PhotoStudio', action: 'labEraserObjects', metadata: { engine: 'lab-platform' } },
        })
        setObjects({ imageUrl, status: 'unavailable' })
      }
    }, LAB_OBJECTS_UPLOAD_DELAY_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [isEnabled, imageUrl, detect, forget])

  const getDetectedSource = useCallback((url: string) => finishedRef.current.get(url), [])

  const isShown = isEnabled && objects?.imageUrl === imageUrl && !(isSilent && objects.status === 'loading')

  return {
    /** The objects of the photo on stage while on — never another photo's, and no 'loading' for a silent detection */
    objects: isShown ? objects : null,
    /** The upload and finished object detection of a photo, for an erase of that photo to reuse; none while it runs */
    getDetectedSource,
  }
}
